import { useEffect, useState, useRef } from 'react';
import { ideaBankAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { StellarSelect } from './StellarFormControls';
import { AssigneeMultiSelect } from './AssigneeMultiSelect';
import { AutoGrowTextarea } from './AutoGrowTextarea';
import { AttachmentsField } from './AttachmentsField';
import { ErrorBoundary } from './ErrorBoundary';
import { Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

/**
 * IdeaBankFormDialog
 * ───────────────────
 * New / Edit dialog for an Idea Bank item. Mirrors the shape of the
 * regular New Task modal but is intentionally lighter:
 *
 *   - NO due_date field (idea bank items are explicitly "no deadline")
 *   - Adds `category` (free-text + autocomplete from prior categories)
 *   - Supports attachments (simple name/url list — pasted or typed)
 *
 * Owner / Admin / Manager / Team can ALL create idea bank items. Backend
 * RBAC restricts who can list/edit/delete each item.
 */
export function IdeaBankFormDialog({
  open,
  onOpenChange,
  workspaceId,
  members = [],
  clients = [],
  currentUserId = '',
  editingItem = null,        // when set, dialog is in edit mode
  onSaved,
}) {
  const initialState = () => ({
    title: '',
    description: '',
    assigned_to: currentUserId ? [currentUserId] : [],
    client_group_id: '',
    priority: 'medium',
    category: '',
    attachments: [],
  });
  const [item, setItem] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCategoryHints, setShowCategoryHints] = useState(false);
  const categoryRef = useRef(null);

  // Reset when opening / closing or when switching editingItem.
  useEffect(() => {
    if (!open) {
      setItem(initialState());
      setShowCategoryHints(false);
      return;
    }
    if (editingItem) {
      setItem({
        title: editingItem.title || '',
        description: editingItem.description || '',
        assigned_to: editingItem.assigned_to || [],
        client_group_id: editingItem.client_group_id || '',
        priority: editingItem.priority || 'medium',
        category: editingItem.category || '',
        attachments: editingItem.attachments || [],
      });
    } else {
      setItem(initialState());
    }
    // Fetch category autocomplete list every open so newly created
    // categories from this session are reflected.
    if (workspaceId) {
      ideaBankAPI.categories(workspaceId)
        .then((r) => setCategories(r.data?.categories || []))
        .catch(() => setCategories([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingItem?.id]);

  const filteredCategoryHints = (() => {
    const q = (item.category || '').trim().toLowerCase();
    if (!q) return categories.slice(0, 8);
    return categories.filter((c) => c.toLowerCase().includes(q) && c.toLowerCase() !== q).slice(0, 8);
  })();

  const submit = async () => {
    if (!item.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        workspace_id: workspaceId,
        title: item.title.trim(),
        description: item.description || '',
        priority: item.priority || 'medium',
        assigned_to: item.assigned_to || [],
        client_group_id: item.client_group_id || null,
        category: (item.category || '').trim(),
        attachments: (item.attachments || []).filter((a) => (a.name || a.url)),
      };
      if (editingItem) {
        const res = await ideaBankAPI.update(workspaceId, editingItem.id, payload);
        toast.success('Idea updated');
        onOpenChange(false);
        if (onSaved) onSaved(res.data);
      } else {
        const res = await ideaBankAPI.create(payload);
        toast.success('Saved to Idea Bank');
        onOpenChange(false);
        if (onSaved) onSaved(res.data);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save idea');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[88vh] overflow-y-auto bg-card border-border/40" data-testid="idea-bank-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[17px] flex items-center gap-2">
            <Lightbulb className="h-4 w-4 text-gold" />
            {editingItem ? 'Edit Idea' : 'New Idea'}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-muted-foreground/60">
            Save an idea for later. No due date — it stays in the Idea Bank until you Activate it.
          </DialogDescription>
        </DialogHeader>
        <ErrorBoundary label="the Idea Bank form">
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-[12px] text-foreground/70">Title *</Label>
              <Input
                value={item.title}
                onChange={(e) => setItem((p) => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Hire a senior video editor for Q3"
                className="h-10 text-[14px] bg-foreground/[0.02] border-border/30"
                autoFocus
                data-testid="idea-bank-title"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[12px] text-foreground/70">Description</Label>
              <AutoGrowTextarea
                value={item.description}
                onChange={(e) => setItem((p) => ({ ...p, description: e.target.value }))}
                placeholder="Why this matters, what it would look like once started, links to references…"
                minRows={6}
                data-testid="idea-bank-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[12px] text-foreground/70">Assignees</Label>
                <AssigneeMultiSelect
                  members={members}
                  selected={item.assigned_to}
                  onChange={(ids) => setItem((p) => ({ ...p, assigned_to: ids }))}
                  showRole
                  testIdPrefix="idea-bank-assignee"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[12px] text-foreground/70">Client (optional)</Label>
                <StellarSelect
                  value={item.client_group_id || '__none__'}
                  onValueChange={(v) => setItem((p) => ({ ...p, client_group_id: v === '__none__' ? '' : v }))}
                  options={[{ value: '__none__', label: 'No client' }, ...clients.map((c) => ({ value: c.id, label: c.name }))]}
                  testId="idea-bank-client"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[12px] text-foreground/70">Priority</Label>
                <div className="flex gap-1.5">
                  {[
                    { v: 'high', l: 'High', cls: 'border-red-500/60 text-red-400 bg-red-500/15', dot: 'bg-red-500' },
                    { v: 'medium', l: 'Med', cls: 'border-gold/60 text-gold bg-gold/15', dot: 'bg-gold' },
                    { v: 'low', l: 'Low', cls: 'border-foreground/30 text-muted-foreground/70 bg-foreground/[0.05]', dot: 'bg-foreground/30' },
                  ].map((p) => {
                    const isSel = item.priority === p.v;
                    return (
                      <button
                        key={p.v}
                        type="button"
                        onClick={() => setItem((prev) => ({ ...prev, priority: p.v }))}
                        className={`flex-1 py-2 rounded-lg text-[12px] font-semibold border-2 transition-all flex items-center justify-center gap-1.5 ${isSel ? p.cls : 'border-border/20 text-muted-foreground/40'}`}
                        data-testid={`idea-bank-priority-${p.v}`}
                        aria-pressed={isSel}
                      >
                        <span className={`h-2 w-2 rounded-full ${isSel ? p.dot : 'bg-foreground/10'}`} />
                        {p.l}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1.5 relative" ref={categoryRef}>
                <Label className="text-[12px] text-foreground/70">Category</Label>
                <Input
                  value={item.category}
                  onChange={(e) => setItem((p) => ({ ...p, category: e.target.value }))}
                  onFocus={() => setShowCategoryHints(true)}
                  onBlur={() => setTimeout(() => setShowCategoryHints(false), 150)}
                  placeholder="e.g. Hiring, AI Tools, Operations"
                  className="h-10 text-[13px] bg-foreground/[0.02] border-border/30"
                  data-testid="idea-bank-category"
                />
                {showCategoryHints && filteredCategoryHints.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 bg-card border border-border/30 rounded-md shadow-lg z-50 max-h-[180px] overflow-y-auto" data-testid="idea-bank-category-suggestions">
                    {filteredCategoryHints.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); setItem((p) => ({ ...p, category: c })); setShowCategoryHints(false); }}
                        className="w-full text-left px-3 py-1.5 text-[12px] hover:bg-foreground/[0.04]"
                        data-testid={`idea-bank-category-suggestion-${c}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              {/* 2026-06-16 — File attachments (replaces the old link-only
                  picker). Uploads to /api/media/upload immediately so the
                  metadata is included in the saved idea. */}
              <AttachmentsField
                value={item.attachments}
                onChange={(next) => setItem((p) => ({ ...p, attachments: next }))}
                workspaceId={workspaceId}
                testIdPrefix="idea-bank-attachments"
              />
            </div>
          </div>
        </ErrorBoundary>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="idea-bank-cancel">Cancel</Button>
          <Button
            className="bg-gold/10 hover:bg-gold/20 text-gold border-0"
            onClick={submit}
            disabled={submitting || !item.title.trim()}
            data-testid="idea-bank-submit"
          >
            {submitting ? 'Saving…' : (editingItem ? 'Save changes' : 'Save to Idea Bank')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default IdeaBankFormDialog;
